import React from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const Press = () => {
  const pressReleases = [
    {
      title: 'HireBroker Launches Revolutionary Property Search Platform',
      date: 'October 10, 2024',
      excerpt: 'New platform combines AI-powered search with expert local knowledge to transform property hunting in Uganda.',
      category: 'Product Launch',
      link: '#',
      featured: true
    },
    {
      title: 'Partnership with Leading Real Estate Developers Announced',
      date: 'September 15, 2024',
      excerpt: 'Strategic alliance brings exclusive access to premium properties across Kampala and major urban centers.',
      category: 'Partnership',
      link: '#'
    },
    {
      title: 'Mobile App Reaches 50+ Downloads on Google Play',
      date: 'November 2024',
      excerpt: 'The HireBroker Android app is live on Google Play, where Google currently lists 50+ downloads.',
      category: 'Milestone',
      link: 'https://play.google.com/store/apps/details?id=com.hirebroker.app&hl=en'
    },
    {
      title: 'HireBroker Expands to Three New Ugandan Cities',
      date: 'July 18, 2024',
      excerpt: 'Service expansion brings our innovative platform to Entebbe, Jinja, and Mbale markets.',
      category: 'Expansion',
      link: '#'
    },
    {
      title: 'Industry Recognition: Best Real Estate Tech Startup 2024',
      date: 'June 30, 2024',
      excerpt: 'HireBroker honored at Uganda Tech Awards for innovative contributions to real estate technology.',
      category: 'Awards',
      link: '#'
    },
    {
      title: 'New Investment Round Raises $2.5M for Market Expansion',
      date: 'May 15, 2024',
      excerpt: 'Funding will accelerate platform development and expand operations across East Africa.',
      category: 'Funding',
      link: '#'
    }
  ];

  const mediaKit = [
    { name: 'Company Logo (PNG)', size: '2.5 MB', download: '#', icon: 'Image' },
    { name: 'Brand Guidelines (PDF)', size: '5.1 MB', download: '#', icon: 'FileText' },
    { name: 'High-resolution Photos', size: '15.3 MB', download: '#', icon: 'Camera' },
    { name: 'Press Release Template', size: '245 KB', download: '#', icon: 'File' },
    { name: 'Company Factsheet', size: '1.2 MB', download: '#', icon: 'Info' },
    { name: 'Executive Bios', size: '3.8 MB', download: '#', icon: 'Users' }
  ];

  const stats = [
    { number: '50+', label: 'Press Releases' },
    { number: '25+', label: 'Media Mentions' },
    { number: '15+', label: 'Awards Won' },
    { number: '100K+', label: 'Monthly Readers' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-primary/10 to-secondary/5">
          <div className="absolute inset-0 opacity-40" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"), radial-gradient(circle at 18% 22%, rgba(59,130,246,0.12), transparent 20%), radial-gradient(circle at 82% 18%, rgba(236,72,153,0.14), transparent 22%)`
          }}></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="absolute -right-16 top-24 w-72 h-72 rounded-full bg-primary/20 blur-3xl"></div>
            <div className="absolute -left-16 bottom-16 w-64 h-64 rounded-full bg-secondary/20 blur-3xl"></div>
            <Breadcrumbs />
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
                Press Center
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
                Stories That
                <span className="block text-primary">Shape Our Future</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Stay informed with the latest news, announcements, and insights from HireBroker.
                Discover how we're transforming the real estate landscape in East Africa.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-card/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2 group-hover:scale-110 transition-transform duration-300">
                    {stat.number}
                  </div>
                  <div className="text-muted-foreground font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Release */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-3xl p-8 md:p-12">
              <div className="max-w-4xl">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                    Featured Release
                  </span>
                  <span className="text-muted-foreground">{pressReleases[0].date}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  {pressReleases[0].title}
                </h2>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  {pressReleases[0].excerpt}
                </p>
                <button className="bg-primary text-primary-foreground px-8 py-4 rounded-xl hover:bg-primary/90 transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl">
                  Read Full Release
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Press Releases Grid */}
        <section className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-4">Latest Press Releases</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stay updated with our latest announcements, product launches, and company milestones
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pressReleases.slice(1).map((release, index) => (
                <div key={index} className="bg-card rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                      {release.category}
                    </span>
                    <span className="text-muted-foreground text-sm">{release.date}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {release.title}
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">{release.excerpt}</p>
                  <a
                    href={release.link}
                    className="inline-flex items-center text-primary hover:text-primary/80 font-semibold group-hover:translate-x-1 transition-transform"
                  >
                    Read More
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Media Kit & Contact */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Media Kit */}
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-8">Media Kit</h2>
                <div className="bg-card rounded-2xl p-8 shadow-sm">
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    Download our official branding materials, high-resolution images, and press resources
                    to create compelling stories about HireBroker.
                  </p>

                  <div className="space-y-4">
                    {mediaKit.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {item.icon === 'Image' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />}
                              {item.icon === 'FileText' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />}
                              {item.icon === 'Camera' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z" />}
                              {item.icon === 'File' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />}
                              {item.icon === 'Info' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
                              {item.icon === 'Users' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />}
                            </svg>
                          </div>
                          <div>
                            <p className="font-semibold text-foreground">{item.name}</p>
                            <p className="text-sm text-muted-foreground">{item.size}</p>
                          </div>
                        </div>
                        <a
                          href={item.download}
                          className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Press Contact */}
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-8">Press Inquiries</h2>
                <div className="bg-card rounded-2xl p-8 shadow-sm">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Get in Touch</h3>
                    <p className="text-muted-foreground">Our media relations team is here to help</p>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">Email</p>
                        <p className="text-primary">press@hirebroker.co</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">Phone</p>
                        <p className="text-muted-foreground">+256 774 509 859</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">Address</p>
                        <p className="text-muted-foreground">Kampala, Uganda</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-20 bg-gradient-to-r from-primary/5 to-secondary/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">Stay in the Loop</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Subscribe to our press newsletter and be the first to know about our latest announcements,
              product updates, and company news.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button className="px-8 py-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl whitespace-nowrap">
                Subscribe
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Press;